import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Upload, Trash2, RefreshCw, Star, Search, ImageOff,
  Plus, Pencil, X, GripVertical, Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuthStore, getLocalized, useLanguageStore } from '../store';
import { SEO } from '../components/SEO';
import { toast } from 'sonner';
import TranslateAllButton from '../components/admin/TranslateAllButton';
import AdminHeader from '../components/admin/AdminHeader';
import { useTranslation } from '../i18n/translations';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_LABEL = {
  kitchens: 'Kitchens',
  wardrobes: 'Wardrobes',
  doors: 'Doors',
  wall_panels: 'Wall Panels',
  soft_furniture: 'Soft Furniture',
  ready_solutions: 'Ready Solutions',
  custom_furniture: 'Custom',
};

const DOOR_SUBCATEGORY_LABEL = {
  invisible_doors: 'Invisible Doors',
  sliding_partitions: 'Sliding Partitions',
  pivot_doors: 'Pivot Doors',
};

const EMPTY_PRODUCT = {
  sku: '',
  name: { en: '', nl: '', ro: '', ru: '' },
  description: { en: '', nl: '', ro: '', ru: '' },
  material: { en: '', nl: '', ro: '', ru: '' },
  finish: { en: '', nl: '', ro: '', ru: '' },
  category: 'kitchens',
  subcategory: '', 
  collection_id: '',
  price: 0,
  price_includes_vat: false,
  is_featured: false,
  is_active: true,
  tech_sheet_url: '',
  tech_sheet_public_id: '',
  tech_sheet_filename: '',
  model_3d_url: '',
  model_3d_public_id: '',
  model_3d_filename: '',
  materials: [],
  finishes: [],
  matrix_title: { en: '', nl: '', ro: '', ru: '' },
  matrix_sub: { en: '', nl: '', ro: '', ru: '' },
  custom_sizes_title: { en: '', nl: '', ro: '', ru: '' },
  custom_sizes_desc: { en: '', nl: '', ro: '', ru: '' },
  sku_label: { en: '', nl: '', ro: '', ru: '' },
  base_material_label: { en: '', nl: '', ro: '', ru: '' },
  base_coating_label: { en: '', nl: '', ro: '', ru: '' },
  alt_materials_label: { en: '', nl: '', ro: '', ru: '' },
  alt_finishes_label: { en: '', nl: '', ro: '', ru: '' },
  dimensions: { width: '', depth: '', height: '', weight: '' }
};

const MAX_RAW_SIZE = 100 * 1024 * 1024;
const FORBIDDEN_EXT = new Set(['exe', 'sh', 'bat', 'cmd', 'msi', 'app']);
const MODEL_3D_EXT = ['obj', 'fbx', 'max', '3ds', 'zip', 'glb', 'gltf'];

const optimizedUrl = (url) => {
  if (!url || !url.includes('/upload/')) return url;
  return url.replace('/upload/', '/upload/w_400,h_500,c_fill,q_auto,f_auto/');
};

async function uploadToCloudinary(file, token, folder = 'products') {
  const sigRes = await fetch(`${API}/api/cloudinary/signature?folder=${folder}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!sigRes.ok) throw new Error('Signature failed');
  const sig = await sigRes.json();
  const form = new FormData();
  form.append('file', file);
  form.append('api_key', sig.api_key);
  form.append('timestamp', sig.timestamp);
  form.append('signature', sig.signature);
  form.append('folder', sig.folder);
  const upRes = await fetch(`https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`, {
    method: 'POST', body: form,
  });
  if (!upRes.ok) throw new Error('Upload failed');
  return await upRes.json();
}

async function uploadRawAsset(file, token, folder = 'products') {
  const sigRes = await fetch(`${API}/api/cloudinary/signature?folder=${folder}&resource_type=raw`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!sigRes.ok) throw new Error('Signature failed');
  const sig = await sigRes.json();
  const form = new FormData();
  form.append('file', file);
  form.append('api_key', sig.api_key);
  form.append('timestamp', sig.timestamp);
  form.append('signature', sig.signature);
  form.append('folder', sig.folder);
  const upRes = await fetch(`https://api.cloudinary.com/v1_1/${sig.cloud_name}/raw/upload`, {
    method: 'POST', body: form,
  });
  if (!upRes.ok) throw new Error('Upload failed');
  return await upRes.json();
}

function validateRawFile(file, allowedExt) {
  if (file.size > MAX_RAW_SIZE) return `${file.name} exceeds 100 MB`;
  const ext = (file.name.split('.').pop() || '').toLowerCase();
  if (FORBIDDEN_EXT.has(ext)) return `File type .${ext} is not allowed`;
  if (allowedExt && !allowedExt.includes(ext)) {
    return `File type .${ext} not supported. Allowed: ${allowedExt.join(', ')}`;
  }
  return null;
}

const RawAssetField = ({ label, accept, allowedExt, token, url, filename, onChange, testid }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handlePick = async (file) => {
    if (!file) return;
    const err = validateRawFile(file, allowedExt);
    if (err) { toast.error(err); return; }
    setUploading(true);
    try {
      const up = await uploadRawAsset(file, token, 'products');
      onChange({ secure_url: up.secure_url, public_id: up.public_id, filename: file.name });
      toast.success(`${label} uploaded`);
    } catch (e) {
      toast.error(e.message || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="border border-neutral-200 p-4 bg-white text-left rounded-none">
      <p className="uppercase tracking-wider text-neutral-400 font-mono text-[10px] mb-2">{label}</p>
      <input
        ref={fileRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => handlePick(e.target.files?.[0])}
        data-testid={`${testid}-input`}
      />
      {url ? (
        <div className="flex items-center gap-2">
          <a href={url} target="_blank" rel="noopener noreferrer" className="flex-1 min-w-0 text-sm truncate underline hover:no-underline text-gray-900 font-light" data-testid={`${testid}-link`}>
            {filename || 'View file'}
          </a>
          <Button variant="outline" size="sm" className="rounded-none text-xs" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid={`${testid}-replace`}>
            Replace
          </Button>
          <Button variant="outline" size="sm" onClick={() => onChange(null)} className="text-destructive rounded-none" data-testid={`${testid}-remove`}>
            <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
          </Button>
        </div>
      ) : (
        <Button variant="outline" size="sm" className="rounded-none text-xs" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid={`${testid}-upload`}>
          <Upload className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />
          {uploading ? 'Uploading…' : 'Upload'}
        </Button>
      )}
      <p className="text-[10px] font-mono text-neutral-400 mt-2">Max 100 MB</p>
    </div>
  );
};

const FormLangSwitcher = ({ active, onChange }) => (
  <div className="flex items-center gap-1.5 text-[10px] tracking-widest font-mono text-neutral-400 select-none">
    {['en', 'nl', 'ro', 'ru'].map((lang, idx) => (
      <React.Fragment key={lang}>
        {idx > 0 && <span className="text-neutral-200">·</span>}
        <button
          key={lang}
          type="button"
          onClick={() => onChange(lang)}
          className={`uppercase transition-all px-2 py-0.5 border rounded-none ${
            active === lang 
              ? 'text-black font-bold border-neutral-300 bg-white shadow-sm' 
              : 'border-transparent text-neutral-400 hover:text-black'
          }`}
        >
          {lang}
        </button>
      </React.Fragment>
    ))}
  </div>
);

const ProductFormModal = ({ open, onClose, onSaved, product, token, collections }) => {
  const { language } = useLanguageStore();
  const { t } = useTranslation(language);
  const isEdit = Boolean(product?.id);
  const [form, setForm] = useState(EMPTY_PRODUCT);
  const [saving, setSaving] = useState(false);
  const [activeTabLang, setActiveTabLang] = useState('en');

  useEffect(() => {
    if (open) {
      if (isEdit) {
        setForm({
          sku: product.sku || '',
          name: { en: '', nl: '', ro: '', ru: '', ...(product.name || {}) },
          description: { en: '', nl: '', ro: '', ru: '', ...(product.description || {}) },
          material: { en: '', nl: '', ro: '', ru: '', ...(product.material || {}) },
          finish: { en: '', nl: '', ro: '', ru: '', ...(product.finish || {}) },
          category: product.category || 'kitchens',
          subcategory: product.subcategory || '', 
          collection_id: product.collection_id || '',
          price: product.price || 0,
          price_includes_vat: !!product.price_includes_vat,
          is_featured: !!product.is_featured,
          is_active: product.is_active !== false,
          tech_sheet_url: product.tech_sheet_url || '',
          tech_sheet_public_id: product.tech_sheet_public_id || '',
          tech_sheet_filename: product.tech_sheet_filename || '',
          model_3d_url: product.model_3d_url || '',
          model_3d_public_id: product.model_3d_public_id || '',
          model_3d_filename: product.model_3d_filename || '',
          materials: Array.isArray(product.materials) ? product.materials : [],
          finishes: Array.isArray(product.finishes) ? product.finishes : [],
          matrix_title: { en: '', nl: '', ro: '', ru: '', ...(product.matrix_title || {}) },
          matrix_sub: { en: '', nl: '', ro: '', ru: '', ...(product.matrix_sub || {}) },
          custom_sizes_title: { en: '', nl: '', ro: '', ru: '', ...(product.custom_sizes_title || {}) },
          custom_sizes_desc: { en: '', nl: '', ro: '', ru: '', ...(product.custom_sizes_desc || {}) },
          sku_label: { en: '', nl: '', ro: '', ru: '', ...(product.sku_label || {}) },
          base_material_label: { en: '', nl: '', ro: '', ru: '', ...(product.base_material_label || {}) },
          base_coating_label: { en: '', nl: '', ro: '', ru: '', ...(product.base_coating_label || {}) },
          alt_materials_label: { en: '', nl: '', ro: '', ru: '', ...(product.alt_materials_label || {}) },
          alt_finishes_label: { en: '', nl: '', ro: '', ru: '', ...(product.alt_finishes_label || {}) },
          dimensions: { 
            width: '', depth: '', height: '', weight: '', 
            ...(product.dimensions && typeof product.dimensions === 'object' ? product.dimensions : {}) 
          }
        });
      } else {
        setForm(EMPTY_PRODUCT);
      }
    }
  }, [open, isEdit, product]);

  const handleSave = async () => {
    if (!form.sku.trim()) { toast.error('SKU is required'); return; }
    if (!form.name.en.trim()) { toast.error('Name (EN) is required'); return; }

    setSaving(true);
    try {
      const rawPrice = form.price !== undefined && form.price !== null ? String(form.price).trim() : '0';
      const sanitizedPrice = isNaN(Number(rawPrice)) || rawPrice === '' ? 0 : Number(rawPrice);

      const body = { 
        ...form, 
        price: sanitizedPrice, 
        collection_id: form.collection_id && form.collection_id.trim() ? form.collection_id : null,
        subcategory: form.subcategory && form.subcategory.trim() ? form.subcategory : null
      };
      const url = isEdit ? `${API}/api/admin/products/${product.id}` : `${API}/api/admin/products`;
      const method = isEdit ? 'PATCH' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Save failed');
      }
      toast.success(isEdit ? 'Product updated' : 'Product created');
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-[2px] flex items-start md:items-center justify-center overflow-y-auto p-4" data-testid="product-form-modal">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-background w-full max-w-3xl my-8 md:my-0 border border-neutral-200 rounded-none shadow-xl text-left"
      >
        <div className="flex items-center justify-between border-b border-neutral-200 p-6 bg-white">
          <div>
            <p className="text-xs uppercase tracking-widest text-neutral-400 font-mono mb-1">{isEdit ? 'Edit Module' : 'Initialization'}</p>
            <h2 className="text-2xl font-extralight text-gray-900">{isEdit ? getLocalized(form.name, language) || 'Product' : 'Create Product System'}</h2>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-black p-1 transition-colors" data-testid="product-form-close">
            <X className="w-5 h-5" strokeWidth={1.5} />
          </button>
        </div>
        
        <div className="px-6 py-4 border-b border-neutral-100 flex items-center justify-between bg-neutral-50/50">
          <div className="flex items-center gap-3">
            <span className="text-[10px] uppercase font-bold text-neutral-400 tracking-wider">Language Scope:</span>
            <FormLangSwitcher active={activeTabLang} onChange={(lang) => setActiveTabLang(lang)} />
          </div>
          <TranslateAllButton
            testid="product-translate-all"
            fields={() => [
              { value: form.name, onChange: (v) => setForm((p) => ({ ...p, name: v })) },
              { value: form.description, onChange: (v) => setForm((p) => ({ ...p, description: v })) },
              { value: form.material, onChange: (v) => setForm((p) => ({ ...p, material: v })) },
              { value: form.finish, onChange: (v) => setForm((p) => ({ ...p, finish: v })) },
              { value: form.matrix_title, onChange: (v) => setForm((p) => ({ ...p, matrix_title: v })) },
              { value: form.matrix_sub, onChange: (v) => setForm((p) => ({ ...p, matrix_sub: v })) },
              { value: form.custom_sizes_title, onChange: (v) => setForm((p) => ({ ...p, custom_sizes_title: v })) },
              { value: form.custom_sizes_desc, onChange: (v) => setForm((p) => ({ ...p, custom_sizes_desc: v })) },
              { value: form.sku_label, onChange: (v) => setForm((p) => ({ ...p, sku_label: v })) },
              { value: form.base_material_label, onChange: (v) => setForm((p) => ({ ...p, base_material_label: v })) },
              { value: form.base_coating_label, onChange: (v) => setForm((p) => ({ ...p, base_coating_label: v })) },
              { value: form.alt_materials_label, onChange: (v) => setForm((p) => ({ ...p, alt_materials_label: v })) },
              { value: form.alt_finishes_label, onChange: (v) => setForm((p) => ({ ...p, alt_finishes_label: v })) },
            ]}
          />
        </div>

        {}
        <div className="p-6 bg-white space-y-6 max-h-[calc(100vh-250px)] overflow-y-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-neutral-500 block mb-2">SKU Identifier *</label>
              <Input
                value={form.sku}
                onChange={(e) => setForm({ ...form, sku: e.target.value })}
                placeholder="e.g. AIR-W-001"
                className="rounded-none h-11 bg-white text-gray-900 focus:border-black font-light"
                data-testid="form-sku"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-neutral-500 block mb-2">System Category *</label>
              <select
                value={form.category}
                onChange={(e) => {
                  const nextCat = e.target.value;
                  setForm({ 
                    ...form, 
                    category: nextCat, 
                    subcategory: nextCat === 'doors' ? form.subcategory : '' 
                  });
                }}
                className="h-11 px-3 border border-neutral-200 bg-white text-sm w-full text-gray-900 focus:outline-none focus:border-black rounded-none cursor-pointer font-light"
                data-testid="form-category"
              >
                {Object.entries(CATEGORY_LABEL).map(([id, label]) => (
                  <option key={id} value={id}>{label}</option>
                ))}
              </select>
            </div>

            {form.category === 'doors' && (
              <div className="md:col-span-2 animate-in fade-in slide-in-from-top-1 duration-200">
                <label className="text-xs uppercase tracking-wider font-semibold text-neutral-500 block mb-2">Subcategory (Architectural Specific)</label>
                <select
                  value={form.subcategory}
                  onChange={(e) => setForm({ ...form, subcategory: e.target.value })}
                  className="h-11 px-3 border border-neutral-200 bg-white text-sm w-full text-gray-900 focus:outline-none focus:border-black rounded-none cursor-pointer font-light"
                >
                  <option value="">— No Subcategory / Unassigned —</option>
                  {Object.entries(DOOR_SUBCATEGORY_LABEL).map(([subId, subLabel]) => (
                    <option key={subId} value={subId}>{subLabel}</option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-neutral-500 block mb-2">Collection Node (Optional)</label>
              <select
                value={form.collection_id || ''}
                onChange={(e) => setForm({ ...form, collection_id: e.target.value })}
                className="h-11 px-3 border border-neutral-200 bg-white text-sm w-full text-gray-900 focus:outline-none focus:border-black rounded-none cursor-pointer font-light"
                data-testid="form-collection"
              >
                <option value="">— No Collection / Independent —</option>
                {collections.map((c) => (
                  <option key={c.id} value={c.id}>
                    {getLocalized(c.subtitle, language) || c.slug} ({getLocalized(c.name, language) || c.slug})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-neutral-500 block mb-2">Base Price (€) *</label>
              <div className="flex gap-2">
                <Input
                  type="text"
                  value={form.price === 0 ? '' : form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                  placeholder="Bespoke Calculation / On request"
                  className="rounded-none h-11 bg-white text-gray-900 focus:border-black flex-1 font-light"
                  data-testid="form-price"
                />
                <select
                  value={String(form.price_includes_vat)}
                  onChange={(e) => setForm({ ...form, price_includes_vat: e.target.value === 'true' })}
                  className="h-11 px-3 border border-neutral-200 bg-white text-sm text-gray-900 focus:outline-none focus:border-black rounded-none cursor-pointer font-light"
                >
                  <option value="false">Excl. VAT</option>
                  <option value="true">Incl. VAT</option>
                </select>
              </div>
              <p className="text-[10px] tracking-wider uppercase text-neutral-400 mt-1.5 font-mono">
                Internal reference — leave blank or 0 for tailored project valuation
              </p>
            </div>
          </div>

          <div className="border border-neutral-200 p-5 bg-neutral-50/40 space-y-6">
            <div>
              <h4 className="text-[10px] font-bold tracking-widest uppercase text-neutral-400 mb-3 font-mono">
                Available Materials Base
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                {[
                  { id: 'wood', en: 'Solid Wood' },
                  { id: 'mdf', en: 'MDF Panels' },
                  { id: 'chipboard', en: 'Chipboard / Particleboard' },
                  { id: 'melamine', en: 'Melamine' },
                  { id: 'acrylic', en: 'Acrylic' },
                  { id: 'aluminum', en: 'Aluminum' },
                  { id: 'glass', en: 'Premium Glass' },
                  { id: 'mirror', en: 'Mirror' },
                  { id: 'stone', en: 'Natural Stone' },
                  { id: 'natural_veneer', en: 'Natural Veneer' },
                  { id: 'engineered_veneer', en: 'Modified Veneer' },
                  { id: 'hpl', en: 'HPL Panels' },
                  { id: 'leather', en: 'Natural Leather' },
                  { id: 'eco_leather', en: 'Eco Leather' },
                  { id: 'fabric', en: 'Fabric / Textiles' },
                  { id: 'primer', en: 'Primer' },
                  { id: 'paint', en: 'Paint' },
                  { id: 'lacquer', en: 'Lacquer' }
                ].map((mat) => {
                  const isSelected = form.materials?.includes(mat.id);
                  return (
                    <button
                      key={mat.id}
                      type="button"
                      onClick={() => {
                        const next = isSelected
                          ? form.materials.filter(id => id !== mat.id)
                          : [...(form.materials || []), mat.id];
                        setForm({ ...form, materials: next });
                      }}
                      className={`min-h-11 py-2.5 px-4 border text-left text-xs transition-all flex items-center rounded-none select-none w-full relative ${
                        isSelected 
                          ? 'border-black bg-white text-black font-semibold shadow-sm' 
                          : 'border-neutral-200 bg-neutral-50 text-neutral-400 hover:border-neutral-400 hover:bg-white'
                      }`}
                    >
                      <span className="pr-6 block truncate w-full" title={mat.en}>{mat.en}</span>
                      {isSelected && (
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-bold text-black">✓</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <h4 className="text-[10px] font-bold tracking-widest uppercase text-neutral-400 mb-3 font-mono">
                Surface Texture & Finish
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                {[
                  { id: 'matte', en: 'Matte' },
                  { id: 'glossy', en: 'Glossy' },
                  { id: 'satin', en: 'Satin' },
                  { id: 'textured', en: 'Textured' },
                  { id: 'natural', en: 'Natural' },
                  { id: 'brushed', en: 'Brushed' }
                ].map((fin) => {
                  const isSelected = form.finishes?.includes(fin.id);
                  return (
                    <button
                      key={fin.id}
                      type="button"
                      onClick={() => {
                        const next = isSelected
                          ? form.finishes.filter(id => id !== fin.id)
                          : [...(form.finishes || []), fin.id];
                        setForm({ ...form, finishes: next });
                      }}
                      className={`min-h-11 py-2.5 px-4 border text-left text-xs transition-all flex items-center rounded-none select-none w-full relative ${
                        isSelected 
                          ? 'border-black bg-white text-black font-semibold shadow-sm' 
                          : 'border-neutral-200 bg-neutral-50 text-neutral-400 hover:border-neutral-400 hover:bg-white'
                      }`}
                    >
                      <span className="pr-6 block truncate w-full" title={fin.en}>{fin.en}</span>
                      {isSelected && (
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-bold text-black">✓</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="border border-neutral-200 p-5 bg-white space-y-4">
            <h4 className="text-[11px] font-bold tracking-wider uppercase text-neutral-900 border-b border-neutral-100 pb-2">
              Linguistic Metadata ({activeTabLang.toUpperCase()})
            </h4>
            <div className="space-y-4">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-neutral-400 font-mono block mb-1">Product Title</label>
                <Input
                  value={form.name[activeTabLang] || ''}
                  onChange={(e) => setForm({ ...form, name: { ...form.name, [activeTabLang]: e.target.value } })}
                  className="rounded-none h-11 bg-white text-gray-900 focus:border-black font-light"
                  placeholder="Bespoke System Title"
                  data-testid={`form-name-${activeTabLang}`}
                />
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-wider text-neutral-400 font-mono block mb-1">Architectural Context Description</label>
                <textarea
                  value={form.description[activeTabLang] || ''}
                  onChange={(e) => setForm({ ...form, description: { ...form.description, [activeTabLang]: e.target.value } })}
                  rows={3}
                  className="w-full border border-neutral-200 bg-white text-sm p-3 resize-y text-gray-900 focus:outline-none focus:border-black font-light leading-relaxed"
                  placeholder="Architectural context, structural features, and design options..."
                  data-testid={`form-desc-${activeTabLang}`}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-neutral-400 font-mono block mb-1">Default Material Blueprint</label>
                  <Input
                    value={form.material[activeTabLang] || ''}
                    onChange={(e) => setForm({ ...form, material: { ...form.material, [activeTabLang]: e.target.value } })}
                    className="rounded-none h-11 bg-white text-gray-900 focus:border-black text-sm font-light"
                    placeholder="e.g. Natural Oak / Brushed Brass"
                    data-testid={`form-material-${activeTabLang}`}
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-neutral-400 font-mono block mb-1">Surface Finish</label>
                  <Input
                    value={form.finish[activeTabLang] || ''}
                    onChange={(e) => setForm({ ...form, finish: { ...form.finish, [activeTabLang]: e.target.value } })}
                    className="rounded-none h-11 bg-white text-gray-900 focus:border-black text-sm font-light"
                    placeholder="e.g. Matte Polyurethane RAL 9005"
                    data-testid={`form-finish-${activeTabLang}`}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="border border-neutral-200 p-5 bg-neutral-50/40 space-y-4">
            <h4 className="text-[11px] font-bold tracking-wider uppercase text-neutral-900 border-b border-neutral-100 pb-2">
              Bespoke Matrix & Specs Labels Overrides ({activeTabLang.toUpperCase()})
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-neutral-400 font-mono block mb-1">Matrix Section Title</label>
                <Input
                  value={form.matrix_title?.[activeTabLang] || ''}
                  onChange={(e) => setForm({ ...form, matrix_title: { ...form.matrix_title, [activeTabLang]: e.target.value } })}
                  placeholder="Leave blank for global default"
                  className="rounded-none h-11 bg-white text-sm text-gray-900 font-light"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-neutral-400 font-mono block mb-1">Custom Sizes Tab Title</label>
                <Input
                  value={form.custom_sizes_title?.[activeTabLang] || ''}
                  onChange={(e) => setForm({ ...form, custom_sizes_title: { ...form.custom_sizes_title, [activeTabLang]: e.target.value } })}
                  placeholder="Leave blank for global default"
                  className="rounded-none h-11 bg-white text-sm text-gray-900 font-light"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-neutral-400 font-mono block mb-1">SKU Table Label</label>
                <Input
                  value={form.sku_label?.[activeTabLang] || ''}
                  onChange={(e) => setForm({ ...form, sku_label: { ...form.sku_label, [activeTabLang]: e.target.value } })}
                  placeholder="Default: Model SKU"
                  className="rounded-none h-11 bg-white text-sm text-gray-900 font-light"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-neutral-400 font-mono block mb-1">Base Material Table Label</label>
                <Input
                  value={form.base_material_label?.[activeTabLang] || ''}
                  onChange={(e) => setForm({ ...form, base_material_label: { ...form.base_material_label, [activeTabLang]: e.target.value } })}
                  placeholder="Default: Current Base Material"
                  className="rounded-none h-11 bg-white text-sm text-gray-900 font-light"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-neutral-400 font-mono block mb-1">Base Coating Table Label</label>
                <Input
                  value={form.base_coating_label?.[activeTabLang] || ''}
                  onChange={(e) => setForm({ ...form, base_coating_label: { ...form.base_coating_label, [activeTabLang]: e.target.value } })}
                  placeholder="Default: Current Base Coating"
                  className="rounded-none h-11 bg-white text-sm text-gray-900 font-light"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-neutral-400 font-mono block mb-1">Alternative Materials Heading</label>
                <Input
                  value={form.alt_materials_label?.[activeTabLang] || ''}
                  onChange={(e) => setForm({ ...form, alt_materials_label: { ...form.alt_materials_label, [activeTabLang]: e.target.value } })}
                  placeholder="Default: Alternative Available Materials:"
                  className="rounded-none h-11 bg-white text-sm text-gray-900 font-light"
                />
              </div>
              <div className="md:col-span-2">
                <label className="text-[10px] uppercase tracking-wider text-neutral-400 font-mono block mb-1">Alternative Finishes Heading</label>
                <Input
                  value={form.alt_finishes_label?.[activeTabLang] || ''}
                  onChange={(e) => setForm({ ...form, alt_finishes_label: { ...form.alt_finishes_label, [activeTabLang]: e.target.value } })}
                  placeholder="Default: Alternative Available Finishes / Textures:"
                  className="rounded-none h-11 bg-white text-sm text-gray-900 font-light"
                />
              </div>
              <div className="md:col-span-2">
                <label className="text-[10px] uppercase tracking-wider text-neutral-400 font-mono block mb-1">Matrix Section Subtext Description</label>
                <textarea
                  value={form.matrix_sub?.[activeTabLang] || ''}
                  onChange={(e) => setForm({ ...form, matrix_sub: { ...form.matrix_sub, [activeTabLang]: e.target.value } })}
                  rows={2}
                  placeholder="Detailed explanation of alternative available configurations..."
                  className="w-full border border-neutral-200 bg-white text-sm p-3 text-gray-900 focus:outline-none focus:border-black resize-y font-light leading-relaxed"
                />
              </div>
              <div className="md:col-span-2">
                <label className="text-[10px] uppercase tracking-wider text-neutral-400 font-mono block mb-1">Custom Dimensions Production Notes</label>
                <textarea
                  value={form.custom_sizes_desc?.[activeTabLang] || ''}
                  onChange={(e) => setForm({ ...form, custom_sizes_desc: { ...form.custom_sizes_desc, [activeTabLang]: e.target.value } })}
                  rows={2}
                  placeholder="Height and structural boundaries (e.g. floor-to-ceiling up to 300cm)..."
                  className="w-full border border-neutral-200 bg-white text-sm p-3 text-gray-900 focus:outline-none focus:border-black resize-y font-light leading-relaxed"
                />
              </div>
            </div>
          </div>

          {}
          <div className="border border-neutral-200 p-5 bg-neutral-50/50 space-y-3">
            <h4 className="text-[10px] font-bold tracking-widest uppercase text-neutral-400 font-mono">Technical Architecture Dimensions</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <label className="block text-[9px] uppercase font-semibold text-neutral-400 mb-1">Width (cm)</label>
                <input
                  type="number"
                  value={form.dimensions?.width || ''}
                  onChange={(e) => setForm(p => ({ ...p, dimensions: { ...p.dimensions, width: e.target.value } }))}
                  className="w-full h-10 px-3 border border-neutral-200 text-xs bg-white focus:outline-none focus:border-black text-gray-900 rounded-none font-light"
                  placeholder="e.g. 120"
                />
              </div>
              <div>
                <label className="block text-[9px] uppercase font-semibold text-neutral-400 mb-1">Depth (cm)</label>
                <input
                  type="number"
                  value={form.dimensions?.depth || ''}
                  onChange={(e) => setForm(p => ({ ...p, dimensions: { ...p.dimensions, depth: e.target.value } }))}
                  className="w-full h-10 px-3 border border-neutral-200 text-xs bg-white focus:outline-none focus:border-black text-gray-900 rounded-none font-light"
                  placeholder="e.g. 60"
                />
              </div>
              <div>
                <label className="block text-[9px] uppercase font-semibold text-neutral-400 mb-1">Height (cm)</label>
                <input
                  type="number"
                  value={form.dimensions?.height || ''}
                  onChange={(e) => setForm(p => ({ ...p, dimensions: { ...p.dimensions, height: e.target.value } }))}
                  className="w-full h-10 px-3 border border-neutral-200 text-xs bg-white focus:outline-none focus:border-black text-gray-900 rounded-none font-light"
                  placeholder="e.g. 75"
                />
              </div>
              <div>
                <label className="block text-[9px] uppercase font-semibold text-neutral-400 mb-1">Weight (kg)</label>
                <input
                  type="number"
                  value={form.dimensions?.weight || ''}
                  onChange={(e) => setForm(p => ({ ...p, dimensions: { ...p.dimensions, weight: e.target.value } }))}
                  className="w-full h-10 px-3 border border-neutral-200 text-xs bg-white focus:outline-none focus:border-black text-gray-900 rounded-none font-light"
                  placeholder="e.g. 45"
                />
              </div>
            </div>
          </div>

          <div className="flex items-center gap-6 mt-3">
            <label className="flex items-center gap-2 text-sm text-gray-800 font-normal select-none cursor-pointer">
              <input
                type="checkbox"
                checked={form.is_featured}
                onChange={(e) => setForm({ ...form, is_featured: e.target.checked })}
                data-testid="form-featured"
                className="accent-black w-4 h-4 cursor-pointer"
              />
              Show in Lookbook Spotlight
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-800 font-normal select-none cursor-pointer">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                data-testid="form-active"
                className="accent-black w-4 h-4 cursor-pointer"
              />
              Published & Active
            </label>
          </div>

          <div className="mt-2 pt-6 border-t border-neutral-200">
            <p className="text-xs uppercase tracking-widest text-neutral-400 font-mono mb-4">Trade assets (B2B Protected Packages)</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <RawAssetField
                label="Tech Sheet Blueprint (PDF)"
                accept=".pdf,application/pdf"
                allowedExt={['pdf']}
                token={token}
                url={form.tech_sheet_url}
                filename={form.tech_sheet_filename}
                onChange={(asset) => setForm({
                  ...form,
                  tech_sheet_url: asset?.secure_url || '',
                  tech_sheet_public_id: asset?.public_id || '',
                  tech_sheet_filename: asset?.filename || '',
                })}
                testid="tech-sheet"
              />
              <RawAssetField
                label="3D BIM Model Asset"
                accept=".obj,.fbx,.max,.3ds,.zip,.glb,.gltf"
                allowedExt={MODEL_3D_EXT}
                token={token}
                url={form.model_3d_url}
                filename={form.model_3d_filename}
                onChange={(asset) => setForm({
                  ...form,
                  model_3d_url: asset?.secure_url || '',
                  model_3d_public_id: asset?.public_id || '',
                  model_3d_filename: asset?.filename || '',
                })}
                testid="model-3d"
              />
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-neutral-200 p-6 bg-white">
          <Button variant="outline" className="rounded-none text-xs" onClick={onClose} disabled={saving} data-testid="product-form-cancel">
            {t?.common?.cancel || 'Cancel'}
          </Button>
          <Button onClick={handleSave} disabled={saving} className="bg-black text-white hover:bg-neutral-800 px-6 text-xs rounded-none uppercase tracking-widest h-10 transition-colors" data-testid="product-form-save">
            {saving ? 'Saving…' : (isEdit ? 'Save Changes' : 'Initialize')}
          </Button>
        </div>
      </motion.div>
    </div>
  );
};

const ProductImageStrip = ({ product, token, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const [items, setItems] = useState(product.images || []);
  const fileRef = useRef(null);

  useEffect(() => { setItems(product.images || []); }, [product.images]);

  const handleFiles = async (files) => {
    if (!files || !files.length) return;
    const valid = Array.from(files).filter((f) => {
      if (!f.type.startsWith('image/')) { toast.error(`${f.name}: not an image`); return false; }
      if (f.size > 15 * 1024 * 1024) { toast.error(`${f.name}: > 15MB`); return false; }
      return true;
    });
    if (!valid.length) return;
    
    setBusy(true);
    try {
      const uploaded = [];
      for (const f of valid) {
        const up = await uploadToCloudinary(f, token);
        uploaded.push({ public_id: up.public_id, secure_url: up.secure_url });
      }
      const res = await fetch(`${API}/api/admin/products/${product.id}/images/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ images: uploaded }),
      });
      if (!res.ok) throw new Error('Save failed');
      toast.success(`${uploaded.length} image${uploaded.length > 1 ? 's' : ''} added`);
      onChanged();
    } catch (err) {
      toast.error(err.message || 'Upload failed');
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const deleteImage = async (publicId) => {
    if (!publicId) return;
    if (!window.confirm('Remove this image permanently?')) return;
    try {
      const res = await fetch(`${API}/api/admin/products/${product.id}/images?public_id=${encodeURIComponent(publicId)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      toast.success('Image removed');
      onChanged();
    } catch {
      toast.error('Failed to remove');
    }
  };

  const setPrimary = async (publicId) => {
    try {
      const res = await fetch(`${API}/api/admin/products/${product.id}/images/set-primary?public_id=${encodeURIComponent(publicId)}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      toast.success('Primary image set');
      onChanged();
    } catch {
      toast.error('Failed to update');
    }
  };

  const persistOrder = async (orderedItems) => {
    const order = orderedItems.map((i) => i.public_id).filter(Boolean);
    if (!order.length) return;
    try {
      const res = await fetch(`${API}/api/admin/products/${product.id}/images/reorder`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ order }),
      });
      if (!res.ok) throw new Error();
      toast.success('Order saved');
      onChanged();
    } catch {
      toast.error('Failed to reorder');
      setItems(product.images || []);
    }
  };

  const handleDragStart = (idx) => () => setDragIdx(idx);
  const handleDragOver = (idx) => (e) => { e.preventDefault(); setOverIdx(idx); };
  const handleDrop = (idx) => (e) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) { setDragIdx(null); setOverIdx(null); return; }
    const newItems = [...items];
    const [moved] = newItems.splice(dragIdx, 1);
    newItems.splice(idx, 0, moved);
    setItems(newItems);
    setDragIdx(null);
    setOverIdx(null);
    persistOrder(newItems);
  };

  return (
    <div className="space-y-3 border-t border-neutral-100 pt-4 mt-2">
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
        data-testid={`bulk-file-input-${product.id}`}
      />
      <div className="flex items-center justify-between gap-3">
        <p className="text-[11px] tracking-wider uppercase text-neutral-400 font-mono">
          {items.length === 0 ? 'No images uploaded' : `${items.length} image${items.length > 1 ? 's' : ''} · drag to reorder`}
        </p>
        <Button
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          variant="outline"
          size="sm"
          className="rounded-none text-xs"
          data-testid={`bulk-upload-btn-${product.id}`}
        >
          <Upload className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />
          {busy ? 'Uploading…' : 'Upload Pack'}
        </Button>
      </div>

      {items.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
          {items.map((img, idx) => (
            <div
              key={img.public_id || `${img.url}-${idx}`}
              draggable={Boolean(img.public_id)}
              onDragStart={handleDragStart(idx)}
              onDragOver={handleDragOver(idx)}
              onDrop={handleDrop(idx)}
              onDragEnd={() => { setDragIdx(null); setOverIdx(null); }}
              className={`group relative border border-neutral-200 bg-neutral-50 overflow-hidden flex flex-col transition-all rounded-none ${
                overIdx === idx && dragIdx !== idx ? 'ring-1 ring-black' : ''
              } ${dragIdx === idx ? 'opacity-40' : ''}`}
              data-testid={`img-tile-${product.id}-${idx}`}
            >
              <div className="aspect-square relative w-full overflow-hidden flex-1">
                <img
                  src={optimizedUrl(img.url).replace('h_500', 'h_200').replace('w_400', 'w_200')}
                  alt=""
                  className="w-full h-full object-cover pointer-events-none"
                />
                {img.is_primary && (
                  <span className="absolute top-1 left-1 bg-black text-white px-1.5 py-0.5 text-[8px] tracking-wider uppercase flex items-center gap-0.5">
                    <Star className="w-2 h-2 fill-white" strokeWidth={0} />Primary
                  </span>
                )}
                {img.public_id && (
                  <span className="absolute top-1 right-1 bg-white/90 p-0.5 shadow-sm border border-neutral-200">
                    <GripVertical className="w-3 h-3 text-neutral-900" strokeWidth={1.5} />
                  </span>
                )}
              </div>
              
              <div className="h-8 border-t border-neutral-200 bg-neutral-50 flex items-center justify-around px-1">
                {!img.is_primary && img.public_id ? (
                  <button
                    type="button"
                    onClick={() => setPrimary(img.public_id)}
                    className="text-neutral-400 hover:text-black transition-colors"
                    title="Make main image"
                  >
                    <Star className="w-3.5 h-3.5" strokeWidth={1.5} />
                  </button>
                ) : (
                  <div className="w-3.5 h-3.5" />
                )}
                {img.public_id && (
                  <button
                    type="button"
                    onClick={() => deleteImage(img.public_id)}
                    className="text-neutral-400 hover:text-red-600 transition-colors"
                    title="Remove"
                  >
                    <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const AdminProductsPage = () => {
  const navigate = useNavigate();
  const { language } = useLanguageStore();
  const userFromHook = useAuthStore((s) => s.user);
  const tokenFromHook = useAuthStore((s) => s.token);
  const logout = useAuthStore((s) => s.logout);
  const [hydrated, setHydrated] = useState(false);
  const [products, setProducts] = useState([]);
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  // Стейты для контроля сортировки карточек
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [isSavingOrder, setIsSavingOrder] = useState(false);

  const user = userFromHook || (hydrated ? useAuthStore.getState().user : null);
  const token = tokenFromHook || (hydrated ? useAuthStore.getState().token : null);

  useEffect(() => {
    const id = setTimeout(() => setHydrated(true), 300);
    return () => clearTimeout(id);
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    const u = useAuthStore.getState().user;
    if (!u || u.role !== 'admin') {
      navigate('/admin', { replace: true, state: { from: '/admin/products' } });
    }
  }, [hydrated, navigate]);

  const fetchAll = React.useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [pRes, cRes] = await Promise.all([
        fetch(`${API}/api/admin/products`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/admin/collections`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (pRes.status === 401 || pRes.status === 403) { logout(); navigate('/admin'); return; }
      const pData = await pRes.json();
      const cData = await cRes.json();
      
      // Сортируем локально по sort_order (1, 2, 3...)
      const sortedProds = (pData.products || []).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
      setProducts(sortedProds);
      setCollections(cData.collections || []);
    } catch {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [token, logout, navigate]);

  useEffect(() => { if (hydrated) fetchAll(); }, [hydrated, fetchAll]);

  const handleDelete = async (productId, productName) => {
    if (!window.confirm(`Delete "${productName}" permanently? All its images will be removed from Cloudinary.`)) return;
    try {
      const res = await fetch(`${API}/api/admin/products/${productId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      toast.success('Product deleted');
      fetchAll();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const filtered = useMemo(() => {
    return products.filter((p) => {
      if (filterCategory && p.category !== filterCategory) return false;
      if (search) {
        const q = search.toLowerCase();
        const name = getLocalized(p.name, 'en')?.toLowerCase() || '';
        return name.includes(q) || (p.sku || '').toLowerCase().includes(q);
      }
      return true;
    });
  }, [products, filterCategory, search]);

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    const dragTarget = e.currentTarget;
    setTimeout(() => {
      dragTarget.style.opacity = '0.4';
    }, 0);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const draggedItem = filtered[draggedIndex];
    const targetItem = filtered[index];
    
    if (!draggedItem || !targetItem) return;

    const masterDraggedIdx = products.findIndex(p => p.id === draggedItem.id);
    const masterTargetIdx = products.findIndex(p => p.id === targetItem.id);

    if (masterDraggedIdx !== -1 && masterTargetIdx !== -1) {
      const masterUpdated = [...products];
      const [moved] = masterUpdated.splice(masterDraggedIdx, 1);
      masterUpdated.splice(masterTargetIdx, 0, moved);
      setProducts(masterUpdated);
      setDraggedIndex(index);
    }
  };

  const handleDragEnd = async (e) => {
    e.currentTarget.style.opacity = '1';
    setDraggedIndex(null);
    await saveNewSortOrder();
  };

  const saveNewSortOrder = async () => {
    if (!token) return;
    setIsSavingOrder(true);
    
    const orderedIds = filtered.map(p => p.id);

    // Сквозной мультиязычный словарь уведомлений для сохранения порядка
    const NOTIFICATIONS = {
      en: {
        saved: 'Catalog sorting order updated',
        failed: 'Failed to sync sorting order with MongoDB'
      },
      ru: {
        saved: 'Порядок каталога сохранен',
        failed: 'Не удалось синхронизировать порядок с MongoDB'
      },
      nl: {
        saved: 'Catalogusvolgorde succesvol bijgewerkt',
        failed: 'Synchronisatie van volgorde met MongoDB mislukt'
      },
      ro: {
        saved: 'Ordinea catalogului a fost salvată',
        failed: 'Sincronizarea ordinii cu MongoDB a eșuat'
      }
    };

    const texts = NOTIFICATIONS[language] || NOTIFICATIONS['en'];

    try {
      const res = await fetch(`${API}/api/admin/products/reorder`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ product_ids: orderedIds })
      });

      if (!res.ok) throw new Error();
      toast.success(texts.saved);
    } catch (err) {
      toast.error(texts.failed);
      fetchAll(); 
    } finally {
      setIsSavingOrder(false);
    }
  };

  if (!hydrated) return <div className="min-h-screen flex items-center justify-center text-neutral-400 font-mono text-xs bg-white">Loading lookbook matrix…</div>;
  if (!user || user.role !== 'admin') return null;

  const collectionById = collections.reduce((acc, c) => { acc[c.id] = c; return acc; }, {});

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-foreground font-light text-left pb-24">
      <AdminHeader active="products" />

      {/* Элитные инъекции CSS-эффектов Drag & Drop */}
      <style>{`
        .fedyk-drag-card {
          transition: transform 0.2s cubic-bezier(0.2, 0.8, 0.2, 1), box-shadow 0.2s ease;
        }
        .fedyk-drag-card:active {
          cursor: grabbing !important;
        }
        .fedyk-drag-handle {
          cursor: grab !important;
          transition: color 0.15s ease;
        }
        .fedyk-drag-handle:active {
          cursor: grabbing !important;
          color: #000000 !important;
        }
      `}</style>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 border-b border-neutral-200 pb-6">
          <div>
            <p className="text-[10px] font-medium tracking-[0.2em] uppercase text-neutral-400 mb-2 font-mono">CMS · Lookbook</p>
            <h1 className="text-4xl md:text-5xl font-extralight tracking-tight text-black">Product Blueprints</h1>
            <p className="text-neutral-500 mt-4 max-w-xl text-sm leading-relaxed font-light">
              Create architectural system assets, catalog blueprints, map trade packages, and configure bulk presentation portfolios with dynamic drag-and-drop sorting.
            </p>
          </div>
          
          <div className="flex items-center gap-3 shrink-0">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={fetchAll} 
              disabled={loading} 
              className="h-10 bg-white text-neutral-900 border-neutral-200 text-xs rounded-none px-4 shadow-sm hover:bg-neutral-50 flex items-center transition-colors font-medium uppercase tracking-wider"
              style={{ height: '40px' }}
              data-testid="admin-products-refresh"
            >
              <RefreshCw className={`w-3.5 h-3.5 mr-2 ${loading ? 'animate-spin' : ''}`} strokeWidth={1.5} />Refresh Hub
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => { setEditing(null); setFormOpen(true); }} 
              className="h-10 bg-white text-neutral-900 border-neutral-200 text-xs rounded-none px-4 shadow-sm hover:bg-neutral-50 flex items-center transition-colors font-medium uppercase tracking-wider"
              style={{ height: '40px' }}
              data-testid="admin-products-new"
            >
              <Plus className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />New System
            </Button>
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-3 mb-10 shadow-[0_2px_8px_rgba(0,0,0,0.01)]">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" strokeWidth={1.5} />
            <Input
              type="text"
              placeholder="Search by system name or active SKU token…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-11 pl-12 rounded-none bg-white text-neutral-900 focus-visible:border-black font-light"
              data-testid="admin-products-search"
            />
          </div>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="h-11 px-4 border border-neutral-200 bg-white text-sm min-w-[200px] text-neutral-900 focus:outline-none focus:border-black rounded-none cursor-pointer font-light"
            data-testid="admin-products-filter-category"
          >
            <option value="">All active categories</option>
            {Object.entries(CATEGORY_LABEL).map(([id, label]) => (
              <option key={id} value={id}>{label}</option>
            ))}
          </select>
        </div>

        {/* Индикатор сохранения порядка */}
        <AnimatePresence>
          {isSavingOrder && (
            <motion.div 
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="bg-neutral-900 text-white text-xs px-4 py-2.5 rounded-none mb-6 flex items-center justify-between tracking-wider uppercase font-mono"
            >
              <span className="flex items-center gap-2">
                <Loader2 className="w-3.5 h-3.5 animate-spin text-neutral-400" />
                Synchronizing order with MongoDB...
              </span>
            </motion.div>
          )}
        </AnimatePresence>

        {loading && products.length === 0 ? (
          <div className="py-24 text-center text-neutral-400 text-xs font-mono">Querying portfolio data…</div>
        ) : filtered.length === 0 ? (
          <div className="py-24 text-center text-neutral-500 border border-neutral-200 bg-white rounded-none shadow-sm text-sm font-light">
            No products match the chosen filters criteria.
            <div className="mt-4">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => { setEditing(null); setFormOpen(true); }} 
                className="h-10 bg-white text-neutral-900 border-neutral-200 text-xs rounded-none px-4 shadow-sm hover:bg-neutral-50 flex items-center transition-colors font-medium mx-auto uppercase tracking-wider"
                style={{ height: '40px' }}
              >
                <Plus className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />Create First Product System
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {filtered.map((p, idx) => {
              const images = p.images || [];
              const primary = images.find((i) => i.is_primary) || images[0];
              const coll = p.collection_id ? collectionById[p.collection_id] : null;
              return (
                <div
                  key={p.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, idx)}
                  onDragOver={(e) => handleDragOver(e, idx)}
                  onDragEnd={handleDragEnd}
                  className="border border-neutral-200 p-6 pl-12 grid grid-cols-1 lg:grid-cols-12 gap-6 bg-white rounded-none shadow-sm text-left relative group fedyk-drag-card"
                  data-testid={`product-row-${p.id}`}
                >
                  {/* Левый маркер Drag-and-Drop захвата карточки */}
                  <div 
                    className="absolute left-3 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 p-2 text-neutral-400 hover:text-black fedyk-drag-handle"
                    title="Drag up or down to reorder"
                  >
                    <GripVertical className="w-5 h-5" strokeWidth={1.5} />
                  </div>

                  <div className="lg:col-span-2">
                    <div className="aspect-[4/5] bg-neutral-50 flex items-center justify-center overflow-hidden border border-neutral-100 rounded-none pointer-events-none select-none">
                      {primary ? (
                        <img src={optimizedUrl(primary.url)} alt={getLocalized(p.name, 'en')} className="w-full h-full object-cover" />
                      ) : (
                        <div className="flex flex-col items-center text-neutral-400">
                          <ImageOff className="w-7 h-7 mb-2" strokeWidth={1} />
                          <span className="text-[10px] tracking-wider uppercase font-mono">No blueprint</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="lg:col-span-10 flex flex-col justify-between">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                      <div>
                        <p className="text-[11px] font-mono text-neutral-400 tracking-wide mb-1 uppercase">
                          {CATEGORY_LABEL[p.category] || p.category} · Token: {p.sku}
                          {coll && <span className="ml-2 text-neutral-300">·</span>}
                          {coll && <span className="ml-2 text-neutral-500">{getLocalized(coll.name, language) || coll.slug}</span>}
                          {p.is_featured && <span className="ml-2 bg-neutral-100 text-neutral-800 px-1.5 py-0.5 font-bold uppercase tracking-wider text-[8px]">Spotlight</span>}
                          {p.is_active === false && <span className="ml-2 bg-neutral-900 text-white px-1.5 py-0.5 font-bold uppercase tracking-wider text-[8px]">Draft</span>}
                        </p>
                        <h3 className="text-xl font-light text-neutral-900 tracking-tight">{getLocalized(p.name, language)}</h3>
                        {getLocalized(p.description, language) && (
                          <p className="text-xs text-neutral-500 font-light max-w-2xl mt-1.5 leading-relaxed line-clamp-2">
                            {getLocalized(p.description, language)}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0 pt-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => { setEditing(p); setFormOpen(true); }}
                          className="rounded-none text-xs h-9 bg-white text-neutral-900 border-neutral-200 transition-colors uppercase tracking-wider px-3"
                          data-testid={`edit-btn-${p.id}`}
                        >
                          <Pencil className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(p.id, getLocalized(p.name, 'en'))}
                          className="rounded-none text-xs h-9 bg-white text-red-600 border-neutral-200 transition-colors hover:bg-rose-50 px-3"
                          data-testid={`delete-btn-${p.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                        </Button>
                      </div>
                    </div>

                    <ProductImageStrip product={p} token={token} onChanged={fetchAll} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <AnimatePresence>
        {formOpen && (
          <ProductFormModal
            open={formOpen}
            onClose={() => setFormOpen(false)}
            onSaved={fetchAll}
            product={editing}
            token={token}
            collections={collections}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminProductsPage;
